import { TFormatMessageOptions } from 'src/components/Lead/WhatsApp/utils/types';
import { ICurrentConversation } from '../components/modalTemplates/types';
import { IUser } from 'src/interfaces/user';
import { formatTextByCase } from 'src/modules/helpers/formatTextByCase';
import { UPPERCASE, CAPITALIZE } from 'src/constants/formatTexts';
import { getAreaData } from 'src/utils/areas/getDataAreas';
import { API_TWILIO } from 'src/constants/conversations';
import { processAllTemplateVariables } from './NormalizeVariablesTemplateObject';
import lodash from 'lodash';
import moment from 'moment';
import { DONT_SHOW } from 'src/views/dpxChat/components/constants/modalTemplates';

/**
 * Extracts and processes all template variables from conversation and user data
 * Creates a comprehensive object with all necessary variables for template formatting
 *
 * @param {ICurrentConversation} conversation - The current conversation object containing lead and vehicle data
 * @param {IUser} user - The current user/agent data
 * @returns {TFormatMessageOptions} - Object containing all processed template variables ready for use in templates
 *
 * @example
 * const conversation = { name: 'John Doe', store: { name: 'Store Name' }, vehicle: { make: { name: 'Toyota' }, model: 'Camry' } };
 * const user = { name: 'Agent Smith' };
 * const variables = getVariablesTemplates(conversation, user);
 * // Returns: { storename: 'Store Name', leadagent: 'Agent Smith', leadname: 'John Doe', ... }
 */
export const getVariablesTemplates = (
    conversation: ICurrentConversation,
    user: IUser,
    options?: {
        randomNumber: string;
    }
): TFormatMessageOptions => {
    const { name, store, version, vehicle } = conversation;

    const lastAppointment = lodash.last(conversation?.lead?.appointments);
    const appointmentDate = lastAppointment
        ? moment(lastAppointment?.startDate).format('DD MMMM YYYY - hh:mm a')
        : DONT_SHOW;

    let variablesTemplates = {
        store: DONT_SHOW,
        client: name,
        user: user?.name,
        model: DONT_SHOW,
        vehiclemodel: DONT_SHOW,
        leadname: name,
        useremail: user?.email,
        userphone: user?.phone,
        storename: DONT_SHOW,
        storeAddress: DONT_SHOW,
        storeAddressLink: DONT_SHOW,
        storePhone: DONT_SHOW,
        date: appointmentDate || DONT_SHOW,
        randomNumber: options?.randomNumber,
        leadagent: user?.name,
        makeModel: '',
    };

    if (store && typeof store !== 'string') {
        if (store?.address)
            variablesTemplates.storeAddress = String(store?.address);
        if (store?.location)
            variablesTemplates.storeAddressLink = String(store?.location);
        if (store?.name) variablesTemplates.storename = String(store?.name);

        const areaData = getAreaData({
            area: conversation.area,
            areas: store?.areas,
        });
        const Phone =
            areaData?.API_TWILIO === API_TWILIO
                ? areaData?.phone
                : areaData?.whatsapp;

        if (Phone) variablesTemplates.storePhone = String(Phone);
    }

    let makeName = '';
    if (vehicle && typeof vehicle !== 'string' && vehicle.make) {
        makeName = formatTextByCase({
            text: vehicle.make?.name || '',
            textToFormat: vehicle.make?.isUppercase ? UPPERCASE : CAPITALIZE,
        });
    }

    if (vehicle && typeof vehicle !== 'string') {
        if (vehicle.make && typeof vehicle.make !== 'string') {
            const isUppercase = vehicle?.make?.isUppercase;
            const textToFormat = isUppercase ? UPPERCASE : CAPITALIZE;
            variablesTemplates.makeModel = formatTextByCase({
                text: vehicle.make?.name || '',
                textToFormat,
            });
        }

        if (version && typeof version !== 'string') {
            variablesTemplates.model = String(
                `${makeName} ${vehicle?.model} - ${version?.name}`
            );
            variablesTemplates.vehiclemodel = String(
                `${makeName} ${vehicle?.model} - ${version?.name}`
            );
        } else {
            variablesTemplates.model = String(`${makeName} ${vehicle?.model}`);
            variablesTemplates.vehiclemodel = String(
                `${makeName} ${vehicle?.model}`
            );
        }
    }

    return processAllTemplateVariables(variablesTemplates);
};
